import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { documentService } from '../services/documentService';
import './DocumentManager.css';

const DocumentManager = ({ employeeId, employeeName }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const { hasModulePermission } = useAuth();

  // Documents sont généralement liés au module EMPLOYEES
  const canUpload = hasModulePermission('EMPLOYEES', 'update');
  const canDelete = hasModulePermission('EMPLOYEES', 'delete');

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await documentService.getByEmployeeId(employeeId);
      setDocuments(response.data);
    } catch (err) {
      setError('Erreur lors du chargement des documents');
      console.error('Error loading documents:', err);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (employeeId) {
      loadDocuments();
    }
  }, [employeeId, loadDocuments]);

  const handleFileUpload = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const file = formData.get('file');
    const description = formData.get('description') || '';

    if (!file || file.size === 0) {
      setError('Veuillez sélectionner un fichier');
      return;
    }

    // Validation côté client
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError('Le fichier est trop volumineux. Taille maximale: 10 MB');
      return;
    }

    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];

    if (!allowedTypes.includes(file.type)) {
      setError('Type de fichier non autorisé. Types autorisés: PDF, Images (JPG, PNG), Word, Excel');
      return;
    }

    setUploading(true);
    setError('');
    try {
      const newDocument = await documentService.upload(employeeId, file, description);
      setDocuments([newDocument, ...documents]);
      setShowUploadForm(false);
      e.target.reset();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors de l\'upload du document';
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      const blob = await documentService.download(documentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Erreur lors du téléchargement du document');
      console.error('Error downloading document:', err);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    try {
      await documentService.delete(documentId);
      setDocuments(documents.filter(doc => doc.id !== documentId));
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors de la suppression';
      setError(errorMessage);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR') + ' ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('image')) return '🖼️';
    if (fileType?.includes('word') || fileType?.includes('msword')) return '📝';
    if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return '📊';
    return '📎';
  };

  return (
    <div className="document-manager">
      <div className="document-header">
        <h3>Documents associés - {employeeName}</h3>
        {canUpload && (
          <button
            onClick={() => setShowUploadForm(!showUploadForm)}
            className="btn btn-primary btn-sm"
          >
            {showUploadForm ? 'Annuler' : '+ Ajouter un document'}
          </button>
        )}
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {showUploadForm && canUpload && (
        <div className="upload-form-container">
          <form onSubmit={handleFileUpload} className="upload-form">
            <div className="form-group">
              <label htmlFor="file">Fichier *</label>
              <input
                type="file"
                id="file"
                name="file"
                required
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              />
              <small className="form-hint">
                Types autorisés: PDF, Images (JPG, PNG), Word, Excel. Taille max: 10 MB
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description (optionnel)</label>
              <textarea
                id="description"
                name="description"
                rows="3"
                placeholder="Description du document..."
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={uploading}>
                {uploading ? 'Upload en cours...' : 'Uploader'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowUploadForm(false);
                  setError('');
                }}
                className="btn btn-secondary"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading">Chargement des documents...</div>
      ) : documents.length === 0 ? (
        <div className="empty-state">
          <p>Aucun document associé à cet employé.</p>
        </div>
      ) : (
        <div className="documents-list">
          <table className="documents-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Nom du fichier</th>
                <th>Taille</th>
                <th>Description</th>
                <th>Uploadé par</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.id}>
                  <td className="file-icon">{getFileIcon(doc.fileType)}</td>
                  <td className="file-name">{doc.originalFileName}</td>
                  <td>{formatFileSize(doc.fileSize)}</td>
                  <td>{doc.description || '-'}</td>
                  <td>{doc.uploadedBy}</td>
                  <td>{formatDate(doc.uploadedAt)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleDownload(doc.id, doc.originalFileName)}
                        className="btn-action btn-download"
                        title="Télécharger"
                      >
                        ⬇️
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(doc.id)}
                          className="btn-action btn-delete"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DocumentManager;

